import { toValue } from "vue";
import { sortPromotionsByPriority } from "../utils/index.js";
import { useLog, useRpcCall } from "@scayle/storefront-nuxt/composables";
const sortByPriority = (promotions) => {
  return promotions.toSorted(
    (promotionA, promotionB) => {
      return sortPromotionsByPriority(
        promotionA.promotion,
        promotionB.promotion
      );
    }
  );
};
export function useApplyPromotions({
  getOrderedPromotions = sortByPriority,
  basket
}) {
  const updateBasketItem = useRpcCall("updateBasketItem");
  const log = useLog("useApplyPromotions");
  const applyPromotion = async (promotion, basketItem) => {
    try {
      log.debug(
        `Adding promotion id '${promotion.promotion.id}' to basket item '${basketItem.key}'`
      );
      const { basket: updatedBasket } = await updateBasketItem({
        basketItemKey: basketItem.key,
        update: {
          quantity: basketItem.quantity,
          promotionId: promotion.promotion.id,
          promotionCode: promotion.promotion.code
        }
      });
      basket.value = updatedBasket;
      return updatedBasket;
    } catch (error) {
      log.error(
        `Could not add promotion (${promotion.promotion.id}) to item (${basketItem.key})`,
        error
      );
    }
  };
  const removePromotion = async (basketItem) => {
    try {
      log.debug(
        `Removing promotion from basket item '${basketItem.key}'`
      );
      const { basket: updatedBasket } = await updateBasketItem({
        basketItemKey: basketItem.key,
        update: {
          quantity: basketItem.quantity,
          promotionId: null,
          promotionCode: null
        }
      });
      basket.value = updatedBasket;
      return updatedBasket;
    } catch (error) {
      log.error(
        `Could not remove promotion from item (${basketItem.key})`,
        error
      );
    }
  };
  const removePromotionById = async (promotionId) => {
    log.debug(
      `Removing promotion '${promotionId}' from all basket items`
    );
    const itemsToRemove = (toValue(basket)?.items ?? []).filter((item) => {
      return item?.promotion?.id === promotionId;
    });
    const results = {};
    for (const item of itemsToRemove) {
      results[item.key] = !!await removePromotion(item);
    }
    return results;
  };
  const applyPromotions = async (sourceBasket) => {
    let localBasket = toValue(sourceBasket) ?? basket.value;
    if (!localBasket) {
      return;
    }
    log.debug(
      `Got ${localBasket.applicablePromotions?.length ?? 0} applicable promotions`
    );
    const basketItemLookup = localBasket.items.reduce((lookupTable, item) => {
      lookupTable.set(item.key, item);
      return lookupTable;
    }, /* @__PURE__ */ new Map());
    let hasChanged = false;
    do {
      hasChanged = false;
      const promotions = getOrderedPromotions(
        localBasket.applicablePromotions || []
      );
      for (const promotion of promotions) {
        if (!promotion.itemId) {
          continue;
        }
        const basketItem = basketItemLookup.get(promotion.itemId);
        if (!basketItem) {
          continue;
        }
        const updatedBasket = await applyPromotion(promotion, basketItem);
        localBasket = updatedBasket || localBasket;
        hasChanged = updatedBasket !== void 0;
        break;
      }
    } while (hasChanged);
    basket.value = localBasket;
    return localBasket;
  };
  return { applyPromotions, removePromotion, removePromotionById };
}
